import { useRef, useState, type CSSProperties } from 'react';
import { ACCENT, BORDER, EASE, FONT_MONO, MUTED, SURFACE, TEXT_PRIMARY } from '../../tokens';

export interface VideoItem {
  title: string;
  desc: string;
}

interface VideoScrollerProps {
  title: string;
  videos: readonly VideoItem[];
}

/** Horizontally snap-scrolling video rail with arrow controls. */
export function VideoScroller({ title, videos }: VideoScrollerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateBounds = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  const scroll = (direction: 'left' | 'right'): void => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
    // Re-check bounds once the smooth scroll has settled.
    setTimeout(updateBounds, 350);
  };

  const btnStyle = (disabled: boolean): CSSProperties => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: `1px solid ${disabled ? 'rgba(255,255,255,0.04)' : BORDER}`,
    background: SURFACE,
    color: disabled ? '#333' : TEXT_PRIMARY,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.2s, color 0.2s',
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: '0.65rem',
            color: ACCENT,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            aria-label="Scroll left"
            disabled={atStart}
            onClick={() => scroll('left')}
            style={btnStyle(atStart)}
            onMouseEnter={(e) => {
              if (!atStart) e.currentTarget.style.borderColor = ACCENT;
            }}
            onMouseLeave={(e) => {
              if (!atStart) e.currentTarget.style.borderColor = BORDER;
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            type="button"
            aria-label="Scroll right"
            disabled={atEnd}
            onClick={() => scroll('right')}
            style={btnStyle(atEnd)}
            onMouseEnter={(e) => {
              if (!atEnd) e.currentTarget.style.borderColor = ACCENT;
            }}
            onMouseLeave={(e) => {
              if (!atEnd) e.currentTarget.style.borderColor = BORDER;
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateBounds}
        className="no-scrollbar"
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingTop: 10,
          paddingBottom: 20,
          width: '100%',
        }}
      >
        {videos.map((vid) => (
          <div
            key={vid.title}
            style={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              overflow: 'hidden',
              position: 'relative',
              aspectRatio: '16 / 9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: `border-color 0.3s, transform 0.3s ${EASE}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,107,0,0.25)';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = BORDER;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,107,0,0.1)',
                  border: '1px solid rgba(255,107,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 3 }}>
                {vid.title}
              </div>
              <div style={{ fontSize: '0.7rem', color: MUTED }}>{vid.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
